/**
 * Where an entity's floating health bar sits (q-enemy-healthbar-above-model).
 *
 * Owner, 2026-11-04 (live guest demo): "Sentinel health bar is placed too low and blends in
 * with the character, ensure health bars are always above enemies." The bar was seated at a
 * fixed 2.1 m over the entity's ground point, whatever its model: the tall Sentinel proxy
 * swallowed it at chest height. The anchor is now MEASURED from the rendered model's
 * world-space bounds — its top relative to the entity's origin, plus a margin — once when
 * the entity is built and again when its placeholder swaps to the loaded model. The frame
 * loop only reads the cached value (no per-frame bounding box).
 */

import { Box3, Vector3, type Object3D } from 'three';

/** Clearance between the model's top and the bar's centre (m). */
export const HEALTH_BAR_MARGIN_M = 0.35;

/** The pre-measurement height (m): the historical fixed seat, used until a model is measured. */
export const HEALTH_BAR_FALLBACK_Y = 2.1;

const box = new Box3();
const origin = new Vector3();

/**
 * The bar's height above `anchor`'s origin: the top of `subject`'s world-space bounding box
 * (both read after a world-matrix update) minus the anchor's world y, plus the margin.
 * `subject` is what is drawn (the loaded model, or the whole group for a placeholder);
 * `anchor` is the entity group the bar follows. An empty or non-finite box — nothing drawn
 * yet — yields the fallback, never a bar on the floor.
 */
export function healthBarLocalY(
  subject: Object3D,
  anchor: Object3D = subject,
  margin: number = HEALTH_BAR_MARGIN_M,
): number {
  anchor.updateWorldMatrix(true, false);
  subject.updateWorldMatrix(true, true);
  box.setFromObject(subject);
  const top = box.max.y;
  if (box.isEmpty() || !Number.isFinite(top)) return HEALTH_BAR_FALLBACK_Y;
  anchor.getWorldPosition(origin);
  const local = top - origin.y + margin;
  return Number.isFinite(local) && local > margin ? local : HEALTH_BAR_FALLBACK_Y;
}
